/**
 * Zod validation schemas for the beta feedback layer (Epic 25, Story 25.1).
 *
 * `FeedbackRecordSchema` matches the Firestore wire shape — `createdAt` is a
 * `Date` (mirrors `aarrearkku-schemas.ts`). The web app writes it via
 * `serverTimestamp()`; readers convert `Timestamp` → `Date` before validating.
 *
 * `FeedbackConfigSchema` validates the editable config (cadence, Finnish
 * questions, severity rules). Importing the config validates it fail-loud
 * (see `shared/src/feedback/index.ts`).
 *
 * @see shared/src/types/feedback.ts (matching TypeScript types)
 * @see docs/firestore-schema.md section "15. Feedback Collection"
 */

import { z } from 'zod';

export const FeedbackTypeSchema = z.enum(['story_quality', 'character_creation', 'beta_pulse']);

export const SeveritySchema = z.enum(['none', 'low', 'medium', 'high']);

export const FeedbackStatusSchema = z.enum(['new', 'reviewed', 'actioned', 'dismissed']);

/**
 * Answers keyed by config question id. Single-choice → `string`,
 * multi-choice → `string[]`. Open-ended by design (Decision 6): per-answer
 * correctness is handled by read-time `deriveSeverity`, not here.
 */
export const FeedbackResponsesSchema = z.record(z.union([z.string(), z.array(z.string())]));

/**
 * Persisted feedback record (Firestore wire shape).
 */
export const FeedbackRecordSchema = z.object({
  feedbackType: FeedbackTypeSchema,
  householdId: z.string().min(1),
  storyId: z.string().min(1).optional(),
  characterId: z.string().min(1).optional(),
  responses: FeedbackResponsesSchema,
  comment: z.string().optional(),
  severity: SeveritySchema.default('none'),
  status: FeedbackStatusSchema.default('new'),
  createdAt: z.date(),
});

// ============================================================================
// Config schema
// ============================================================================

export const FeedbackOptionSchema = z.object({
  value: z.string().min(1),
  label: z.string().min(1),
});

export const FeedbackFollowUpSchema = z.object({
  id: z.string().min(1),
  showIf: z.array(z.string().min(1)).min(1),
  prompt: z.string().min(1),
  multi: z.boolean(),
  options: z.array(FeedbackOptionSchema).min(1),
});

export const FeedbackQuestionSchema = z.object({
  id: z.string().min(1),
  prompt: z.string().min(1),
  multi: z.boolean(),
  options: z.array(FeedbackOptionSchema).min(1),
  followUp: FeedbackFollowUpSchema.optional(),
});

export const FeedbackQuestionSetSchema = z.object({
  questions: z.array(FeedbackQuestionSchema).min(1),
  comment: z.boolean(),
});

export const StoryCadenceSchema = z.object({
  firstAfter: z.number().int().positive(),
  repeatEvery: z.number().int().positive(),
  maxPerWeek: z.number().int().positive(),
});

export const CharacterCadenceSchema = z.object({
  lightAfter: z.number().int().positive(),
  detailedAfter: z.number().int().positive(),
});

export const PulseCadenceSchema = z.object({
  afterStory: z.number().int().positive(),
  afterDays: z.number().int().positive(),
  minStories: z.number().int().positive(),
  minDaysBetweenPulses: z.number().int().positive(),
});

export const FeedbackCadenceSchema = z.object({
  story: StoryCadenceSchema,
  character: CharacterCadenceSchema,
  pulse: PulseCadenceSchema,
});

export const SeverityHighRuleSchema = z.object({
  ratingQuestionId: z.string().min(1),
  ratingValues: z.array(z.string().min(1)),
  tagQuestionId: z.string().min(1),
  tagValues: z.array(z.string().min(1)),
});

/** Severity rules keyed by feedback type. MVP ships only `story_quality`. */
export const FeedbackSeverityRulesSchema = z
  .object({
    story_quality: z.object({ high: SeverityHighRuleSchema }).optional(),
    character_creation: z.object({ high: SeverityHighRuleSchema }).optional(),
    beta_pulse: z.object({ high: SeverityHighRuleSchema }).optional(),
  })
  .strict();

export const FeedbackConfigSchema = z.object({
  version: z.number().int().positive(),
  cadence: FeedbackCadenceSchema,
  surveyTitle: z.string().min(1),
  commentPlaceholder: z.string().min(1),
  submitLabel: z.string().min(1),
  skipLabel: z.string().min(1),
  multiSelectHint: z.string().min(1),
  thanksMessage: z.string().min(1),
  questions: z.object({
    story: FeedbackQuestionSetSchema,
    character_light: FeedbackQuestionSetSchema,
    character_detailed: FeedbackQuestionSetSchema,
  }),
  pulseWaves: z.array(FeedbackQuestionSetSchema).min(1),
  severity: FeedbackSeverityRulesSchema,
});
